/**
 * Fuso horário do tenant (Configurações > Gerais > Fuso Horário).
 *
 * Em vez de tocar em cada tela, injetamos o fuso do tenant como padrão nos
 * formatadores nativos (`Date#toLocale*` e `Intl.DateTimeFormat`). Qualquer
 * chamada que já passe `timeZone` continua mandando.
 */

const FALLBACK_TZ = "America/Sao_Paulo";
let tenantTz = FALLBACK_TZ;
let installed = false;

export function getTenantTimezone() {
  return tenantTz;
}

export function setTenantTimezone(tz) {
  if (tz && typeof tz === "string") tenantTz = tz;
}

function withTz(options) {
  if (options && options.timeZone) return options;
  return { ...(options || {}), timeZone: tenantTz };
}

export function installTenantTimezone() {  if (installed || typeof window === "undefined") return;
  installed = true;

  const dateProto = Date.prototype;
  ["toLocaleString", "toLocaleDateString", "toLocaleTimeString"].forEach((name) => {
    const original = dateProto[name];
    dateProto[name] = function patched(locales, options) {
      return original.call(this, locales, withTz(options));
    };
  });

  const OriginalDTF = Intl.DateTimeFormat;
  function PatchedDTF(locales, options) {
    return new OriginalDTF(locales, withTz(options));
  }
  PatchedDTF.prototype = OriginalDTF.prototype;
  PatchedDTF.supportedLocalesOf = OriginalDTF.supportedLocalesOf.bind(OriginalDTF);
  Intl.DateTimeFormat = PatchedDTF;
}

/** Partes da data em um fuso, via Intl (nada de somar offset na mão). */
function partesNoFuso(date, tz) {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const p = {};
  fmt.formatToParts(date).forEach(({ type, value }) => {
    p[type] = value;
  });
  return p;
}

const SEM_FUSO = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/;

/**
 * Valor gravado (UTC "…Z") → texto do input "YYYY-MM-DDTHH:mm" no fuso do tenant.
 *
 * Sem isto o drawer mostrava a hora UTC crua (11:00 para um serviço das 08:00),
 * enquanto a listagem, que usa `toLocaleString`, mostrava o horário certo.
 */
export function paraInputLocal(valor) {
  if (!valor) return "";
  const texto = String(valor);
  // Já está sem fuso: é hora local do tenant, usa como está.
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(texto)) {
    const m = SEM_FUSO.exec(texto);
    return m ? `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}` : "";
  }
  const d = new Date(texto);
  if (Number.isNaN(d.getTime())) return "";
  const p = partesNoFuso(d, tenantTz);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Dia de hoje ("YYYY-MM-DD") no fuso da empresa, não no fuso do navegador. */
export function hojeNoFusoTenant() {
  const p = partesNoFuso(new Date(), tenantTz);
  return `${p.year}-${p.month}-${p.day}`;
}

/** Texto do input (hora do tenant) → ISO em UTC ("…Z"), como o banco espera. */
export function deInputLocalParaISO(valor) {
  if (!valor) return "";
  const texto = String(valor);
  // Já veio com fuso (ex.: chegada prevista, calculada em ISO): não mexe.
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(texto)) {
    const d = new Date(texto);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString();
  }
  const m = SEM_FUSO.exec(texto);
  if (!m) return "";
  const [, ano, mes, dia, hora, min] = m;
  const comoUtc = Date.UTC(+ano, +mes - 1, +dia, +hora, +min, 0);
  // Descobre o offset do fuso NAQUELE instante (resolve horário de verão).
  const p = partesNoFuso(new Date(comoUtc), tenantTz);
  const lidoDeVolta = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return new Date(comoUtc - (lidoDeVolta - comoUtc)).toISOString();
}
